import { CharComp } from '@/components/CharComp'
import { TabPane, Tabs } from '@/components/Tabs'
import { AssetTabs, useAssetFundAssetListComponent } from '@/hooks/components/use-asset-fund-asset-list-component'
import type { HandleMoreListReturn } from '@/hooks/use-handle-more-list'
import { CustomTableLayout } from '@/layouts/CustomTableLayout'
import { PaginationLayout } from '@/layouts/PaginationLayout'
import '@/layouts/trading-pair-quotes-layout.scss'
import { APP_CONFIG, AssetType } from '@/project-config'
import { Search } from '@element-plus/icons-vue'
import { ElCheckbox, ElPagination, ElTableColumn } from 'element-plus'
import { useI18n } from 'i18n-utils'
import { paginationConfig } from 'platform-config'
import { useAssetsStore, useLanguageStore, useResponsive, useStaticTableDataStore, useUserInfoStore } from 'store'
import { ControlledAmount, EmptyData, HocElTable, InputView, LazyLoadingComponent, LoadingComponent, SizedBox } from 'ui-components'
import { VirtualList } from 'ui-components/src/components/VirtualList/VirtualList'
import { DefineComponentProps } from 'utils'
import { AssetListSkeleton } from '../../skeletons/AssetListSkeleton'
import { AssetCell } from './AssetCell'
import './AssetFundAssetList.scss'

type AssetFundAssetList = {
  /** 是否是持仓资产 */
  isHolding?: boolean
  coinFromItemId?: ItemId
  coinToItemId?: ItemId
}

export const AssetFundAssetList = defineComponent<AssetFundAssetList>({
  name: 'AssetFundAssetList',
  props: DefineComponentProps<AssetFundAssetList>(['isHolding', 'coinFromItemId', 'coinToItemId']),
  setup(props) {
    const { isHolding = false } = props
    const { t } = useI18n()

    const assetsStore = useAssetsStore()
    const staticTableStore = useStaticTableDataStore()
    const componentData = useAssetFundAssetListComponent(isHolding)
    const userInfoStore = useUserInfoStore()
    const languageStore = useLanguageStore()
    const responsive = useResponsive()

    const positionList = computed(() => {
      return componentData.digitalCurrencyList.value?.filter((item) => item.all.isGreaterThan(0))
    })

    const currentCoinAssetList = computed(() => {
      return componentData.digitalCurrencyList?.value?.filter((item) => item.itemId === props.coinFromItemId || item.itemId === props.coinToItemId) || []
    })

    const otherNotZeroAssetList = computed(() => {
      return positionList.value?.filter((item) => item.itemId !== props.coinFromItemId && item.itemId !== props.coinToItemId) || []
    })

    const MobileDigitalList = (assetList: ApiResponse.RemainAssetListItem[]) => {
      const newAssetList = assetList?.filter((item) => {
        if (componentData.searchValue.value) {
          const itemInfo = staticTableStore.getItemInfoByItemId(item.itemId)
          if (!itemInfo?.itemName.toUpperCase().includes(componentData.searchValue.value.toUpperCase())) {
            return false
          }
        }

        return true
      })

      const allAssetList = isHolding ? otherNotZeroAssetList.value : newAssetList
      return (
        <LazyLoadingComponent>
          {isHolding && (
            <>
              <div class={'current-coin-asset'}>{t('当前币对资产')}</div>
              <SizedBox height={8} />
              <VirtualList data={currentCoinAssetList.value} itemMinHeight={107} class="mobile-asset-list current-coin-asset-list" noRenderWhenNotInView>
                {{
                  default: (item: { data: ApiResponse.RemainAssetListItem }) => {
                    return <AssetCell asset={item.data} key={item.data.itemId} />
                  },
                }}
              </VirtualList>
              {currentCoinAssetList.value.length === 0 && <EmptyData />}
              <div class="line" />
              <SizedBox height={16} />
              <div class={'current-coin-asset'}>{t('其他非0资产')}</div>
              <SizedBox height={12} />
            </>
          )}
          <VirtualList data={allAssetList} itemMinHeight={107} class="mobile-asset-list" noRenderWhenNotInView>
            {{
              default: (item: { data: ApiResponse.RemainAssetListItem }) => {
                return <AssetCell asset={item.data} key={item.data.itemId} />
              },
            }}
          </VirtualList>
          {allAssetList.length === 0 && <EmptyData />}
        </LazyLoadingComponent>
      )
    }

    const adminMinWidth = computed(() => {
      if (languageStore.isEn) {
        return 230
      }
      if (languageStore.isZH) {
        return 140
      }
      return 'auto'
    })

    const renderDigitalTable = (assetList: ApiResponse.RemainAssetListItem[], tab: AssetType) => {
      return (
        <HocElTable class={'asset-fund-asset-list'} data={assetList} rowKey={(row: ApiResponse.RemainAssetListItem) => row.itemId.toString()}>
          {{
            default: () => (
              <>
                <ElTableColumn label={t('币种')} sortable sortMethod={componentData.sortMethodByItemName}>
                  {{
                    default: (data: { row: ApiResponse.RemainAssetListItem }) => {
                      const itemInfo = staticTableStore.getItemInfoByItemId(data.row.itemId)
                      return (
                        <div class={'coin-wrap'}>
                          <img class={'coin-icon'} width={24} src={itemInfo?.itemDetail.coinUrl} alt="" />
                          <div class={'coin-name-wrap'}>
                            <div class={'coin-name'}>
                              <CharComp class={'from'}>{itemInfo?.itemName.toUpperCase()}</CharComp>
                            </div>
                            <div class={'explain'}>{staticTableStore.getCoinNameByCoinId(data.row.itemId)?.toUpperCase()}</div>
                          </div>
                        </div>
                      )
                    },
                  }}
                </ElTableColumn>
                <ElTableColumn label={t('全部')} sortable sortMethod={componentData.sortMethodByAll}>
                  {{
                    default: (data: { row: ApiResponse.RemainAssetListItem }) => {
                      return <ControlledAmount>{data.row.all?.removeTrailingZero.decimalPadZero(2).formatNumberWithCommas() || '--'}</ControlledAmount>
                    },
                  }}
                </ElTableColumn>
                <ElTableColumn label={t('可用')} sortable sortMethod={componentData.sortMethodByOver}>
                  {{
                    default: (data: { row: ApiResponse.RemainAssetListItem }) => {
                      return <ControlledAmount>{data.row.over?.removeTrailingZero.decimalPadZero(2).formatNumberWithCommas() || '--'}</ControlledAmount>
                    },
                  }}
                </ElTableColumn>
                <ElTableColumn label={t('冻结')} sortable sortMethod={componentData.sortMethodByLock}>
                  {{
                    default: (data: { row: ApiResponse.RemainAssetListItem }) => {
                      return <ControlledAmount>{data.row.lock?.removeTrailingZero.decimalPadZero(2).formatNumberWithCommas() || '--'}</ControlledAmount>
                    },
                  }}
                </ElTableColumn>
                <ElTableColumn align={tab === AssetType.Digital ? 'left' : 'right'} label={t('估值')} sortable sortMethod={componentData.sortMethodByValuation}>
                  {{
                    default: (data: { row: ApiResponse.RemainAssetListItem }) => {
                      return (
                        <ControlledAmount>
                          ≈ {userInfoStore.moneyMark} {data.row.valuation?.formatNumberWithCommas() || '0.00'}
                        </ControlledAmount>
                      )
                    },
                  }}
                </ElTableColumn>
                {tab === AssetType.Digital && !isHolding && (
                  <ElTableColumn label={t('操作')} align="right" width={adminMinWidth.value}>
                    {{
                      default: (data: { row: ApiResponse.RemainAssetListItem }) => {
                        return (
                          <>
                            <span class="btn-text" onClick={() => componentData.onRecharge(data.row)}>
                              {t('充值')}
                            </span>
                            <span class="btn-text" onClick={() => componentData.onWithdraw(data.row)}>
                              {t('提币')}
                            </span>
                            {APP_CONFIG.isExchange && (
                              <span class="btn-text" onClick={() => componentData.onTrade(data.row)}>
                                {t('交易')}
                              </span>
                            )}
                          </>
                        )
                      },
                    }}
                  </ElTableColumn>
                )}
              </>
            ),
            empty: () => <EmptyData />,
          }}
        </HocElTable>
      )
    }

    const DigitalList = (moreData: HandleMoreListReturn<ApiResponse.RemainAssetListItem>, tab: AssetType) => {
      const allAssetList = isHolding
        ? moreData.dataSource.value.filter((item) => {
            return item.itemId !== props.coinFromItemId && item.itemId !== props.coinToItemId
          })
        : moreData.dataSource.value

      return (
        <LazyLoadingComponent>
          <LoadingComponent loading={assetsStore.refreshLoading}>
            {isHolding && (
              <>
                <SizedBox height={8} />
                <div class={'current-coin-asset'}>{t('当前币对资产')}</div>

                {renderDigitalTable(currentCoinAssetList.value || [], tab)}
                <div class="line" />
                <SizedBox height={14} />
                <div class={'current-coin-asset'}>{t('其他非0资产')}</div>
              </>
            )}
            {renderDigitalTable(isHolding ? otherNotZeroAssetList.value : allAssetList, tab)}
            {userInfoStore.isLogin && !isHolding && (
              <PaginationLayout>
                <ElPagination
                  {...paginationConfig}
                  currentPage={moreData.currentPage.value}
                  pageSize={moreData.pageSize.value}
                  total={componentData.currentAssetList.value?.length}
                  pageCount={moreData.pageCount.value}
                  onUpdate:page-size={(size) => {
                    moreData.onUpdatePageSize(size)
                  }}
                  onUpdate:current-page={(page) => {
                    moreData.onUpdateCurrentPage(page)
                    window.scrollTo({
                      top: 215,
                      behavior: 'smooth',
                    })
                  }}
                />
              </PaginationLayout>
            )}
          </LoadingComponent>
        </LazyLoadingComponent>
      )
    }

    return () => {
      if (isHolding) {
        return (
          <div class={'asset-fund-asset-list-layout is-holding'}>
            <div class="trading-pair-quotes-layout">
              <CustomTableLayout>
                {responsive.isMobile && MobileDigitalList(positionList.value || [])}
                {!responsive.isMobile && DigitalList(componentData.digitalMoreData, AssetType.Digital)}
              </CustomTableLayout>
            </div>
          </div>
        )
      }
      return (
        <div class="asset-fund-asset-list-layout">
          <div class="trading-pair-quotes-layout">
            <CustomTableLayout>
              <AssetListSkeleton loading={assetsStore.initLoading}>
                <Tabs
                  class={'primary-tabs'}
                  modelValue={componentData.currentTab.value}
                  tabHeight={54}
                  showDivider={false}
                  tabNavHeight={40}
                  lineWidth={16}
                  headerRight={() => (
                    <div class="filter-box">
                      <InputView v-model={componentData.searchValue.value} prefixIcon={Search} placeholder={t('搜索')} />
                      <ElCheckbox v-model={componentData.hideSmallAmount.value}>{t('隐藏小额资产')}</ElCheckbox>
                    </div>
                  )}
                  onTabChange={(value) => {
                    componentData.currentTab.value = value as AssetType
                  }}
                >
                  {AssetTabs.map((tab) => {
                    const moreData = tab.type === AssetType.Digital ? componentData.digitalMoreData : componentData.fiatMoreData
                    const assetList = tab.type === AssetType.Digital ? componentData.digitalCurrencyList.value : componentData.fiatCurrencyList.value
                    return (
                      <TabPane name={tab.type} label={t(tab.title)}>
                        {responsive.isMobile && MobileDigitalList(assetList || [])}
                        {!responsive.isMobile && DigitalList(moreData, tab.type)}
                      </TabPane>
                    )
                  })}
                </Tabs>
              </AssetListSkeleton>
            </CustomTableLayout>
          </div>
        </div>
      )
    }
  },
})
